import { useCallback, useEffect, useRef, useState } from "react";

// The preview cache refetches HTML itself during startup. Give its in-flight
// iframe time to render before rotating a ticket and remounting the frame.
export function runtimeRecoveryDelay(attempt: number): number {
  return attempt === 0 ? 30_000 : 60_000;
}

export function useRuntimeRecovery(
  key: string | null,
  loaded: boolean,
  recover: () => Promise<void>,
) {
  const [attempts, setAttempts] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [generation, setGeneration] = useState(0);
  const recoverRef = useRef(recover);
  recoverRef.current = recover;
  const currentKeyRef = useRef(key);
  currentKeyRef.current = key;
  const loadedRef = useRef(loaded);
  loadedRef.current = loaded;
  const inFlightRef = useRef<{ key: string } | null>(null);
  const manualRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const retry = useCallback(() => {
    if (!key || inFlightRef.current?.key === key) return;
    manualRef.current = true;
    setGeneration((current) => current + 1);
  }, [key]);

  useEffect(() => {
    setAttempts(0);
    setRetrying(false);
    manualRef.current = false;
  }, [key]);

  useEffect(() => {
    if (loaded) {
      setAttempts(0);
      setRetrying(false);
      return;
    }
    if (!key) return;
    let cancelled = false;
    let timer: number | null = null;
    const schedule = () => {
      if (cancelled || inFlightRef.current?.key === key) return;
      if (document.visibilityState === "hidden" || !navigator.onLine) return;
      const delay = manualRef.current ? 0 : runtimeRecoveryDelay(attempts);
      manualRef.current = false;
      timer = window.setTimeout(async () => {
        timer = null;
        if (cancelled || document.visibilityState === "hidden" || !navigator.onLine || inFlightRef.current?.key === key) return;
        const request = { key };
        inFlightRef.current = request;
        setRetrying(true);
        try {
          await recoverRef.current();
        } catch (error) {
          console.warn("App runtime recovery failed:", error);
        } finally {
          if (inFlightRef.current === request) inFlightRef.current = null;
          if (!cancelled) {
            setRetrying(false);
            setAttempts((current) => current + 1);
          } else if (mountedRef.current && currentKeyRef.current === key && !loadedRef.current) {
            // A replacement App or a visibility change can cancel this timer
            // while its request is still pending. Wake the current schedule.
            setGeneration((current) => current + 1);
          }
        }
      }, delay);
    };
    schedule();
    const resume = () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      schedule();
    };
    window.addEventListener("online", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
      window.removeEventListener("online", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [attempts, generation, key, loaded]);

  return { attempts, retrying, retry };
}
